import { AlertCircle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface CrawledPage {
  url: string;
  title: string;
  status: number;
  depth: number;
  linksCount: number;
}

type CrawlJobStatus = {
  success: boolean;
  status: 'scraping' | 'completed' | 'failed' | 'blocked';
  completed: number;
  total: number;
  creditsUsed: number;
  expiresAt: string;
  startTime?: number;
  isProcessing?: boolean;
  error?: string;
  message?: string;
  pages?: CrawledPage[];
};

interface CrawlProgressDialogProps {
  open: boolean;
  onClose: () => void;
  currentJob: {
    id: string;
    url: string;
    limit: number;
    prefix: string;
    startTime?: number;
    status?: CrawlJobStatus;
    taskId: string;
  } | null;
  elapsedTime: number;
  onStop: () => Promise<void>;
  pages?: CrawledPage[];
}

export default function CrawlProgressDialog({
  open,
  onClose,
  currentJob,
  elapsedTime,
  onStop,
  pages: pagesProp = [],
}: CrawlProgressDialogProps) {
  // Use pages from prop (real-time from API) - this is the authoritative source
  // If pagesProp is explicitly empty [], trust it (backend cleared pages)
  const pages = pagesProp || [];
  // Early return MUST come before any hooks
  if (!currentJob) return null;

  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [virtualProgress, setVirtualProgress] = useState(0);
  const [virtualUploadProgress, setVirtualUploadProgress] = useState(0);

  // Auto-close on completion after 5 seconds
  useEffect(() => {
    if (currentJob?.status?.status === 'completed' && !currentJob?.status?.isProcessing) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentJob?.status?.status, currentJob?.status?.isProcessing, onClose]);

  const status = currentJob.status;
  const completed = status?.completed || 0;
  const total = status?.total || 0;

  // For progress display, we use pages from real-time API polling
  // CRITICAL: Only count pages that are actually present in this response
  // The pages array should reflect real-time crawled pages
  const crawledCount = pages.length;
  const totalCount = total > 0 ? total : 0;

  // Determine if we should use virtual progress
  // isInitializing = no status yet OR no pages and no completed count
  // Key fix: Don't count completed from API - only count actual pages in array
  const isInitializing = !status || crawledCount === 0;
  const isUploadPhase = status?.status === 'completed' && status?.isProcessing;
  const realProgress =
    totalCount > 0 && crawledCount > 0 ? Math.round((crawledCount / totalCount) * 100) : 0;

  // Use virtual progress during initialization (when pages is empty but job just started)
  // Once pages start arriving, switch to real progress based on pages.length
  // During upload phase, use max of real progress and virtual upload progress
  // When upload completes (isProcessing becomes false), show 100%
  const displayProgress = isInitializing
    ? virtualProgress // Show virtual progress while no data yet
    : isUploadPhase
      ? Math.max(realProgress, virtualUploadProgress)
      : status?.status === 'completed' && !status?.isProcessing
        ? 100
        : crawledCount > 0
          ? realProgress // Show real progress once pages arrive
          : virtualProgress; // Keep showing virtual progress until pages arrive

  // Animate virtual progress - never freezes, always moving
  useEffect(() => {
    if (!open) {
      setVirtualProgress(0);
      return;
    }

    const interval = setInterval(() => {
      setVirtualProgress((prev) => {
        // Slow down as we approach limits
        // During init: go up to 30%
        // During processing: go up to 90% (real progress takes over after)
        const limit = isInitializing ? 30 : 90;
        if (prev >= limit) return prev;

        // Slower increments as we get higher
        const increment = prev < 20 ? 0.5 : prev < 50 ? 0.3 : 0.1;
        return Math.min(prev + increment, limit);
      });
    }, 200);

    return () => clearInterval(interval);
  }, [open, isInitializing]);

  // Animate virtual upload progress during S3 upload phase
  useEffect(() => {
    if (!open || !isUploadPhase) {
      setVirtualUploadProgress(0);
      return;
    }

    // Initialize virtual upload progress from current real progress
    if (virtualUploadProgress === 0 && realProgress > 0) {
      setVirtualUploadProgress(realProgress);
    }

    const interval = setInterval(() => {
      setVirtualUploadProgress((prev) => {
        // Cap at 95% to avoid false completion indication
        const limit = 95;
        if (prev >= limit) return prev;

        // Exponential decay: increment slows down as we approach limit
        const increment = (limit - prev) * 0.05; // 5% of remaining distance
        return Math.min(prev + Math.max(increment, 0.1), limit);
      });
    }, 200);

    return () => clearInterval(interval);
  }, [open, isUploadPhase, realProgress, virtualUploadProgress]);

  // Format time as human-readable (e.g., "5s", "1m30s", "1h1m1s")
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h${mins}m${secs}s`;
    } else if (mins > 0) {
      return `${mins}m${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Get status display info
  const getStatusDisplay = () => {
    // Calculate progress percentage based on actual crawled pages
    // Only show percentage when we have real pages data
    const percentage =
      crawledCount > 0 && totalCount > 0 ? Math.round((crawledCount / totalCount) * 100) : 0;
    const progressText = crawledCount > 0 ? ` ${Math.min(percentage, 100)}%` : '';

    if (!status || (status.total === 0 && status.completed === 0)) {
      return {
        text: 'Đang khởi tạo và phân tích website...',
        icon: <Loader2 className="h-5 w-5 animate-spin text-blue-500" />,
        color: 'text-blue-600',
      };
    }

    // Check for S3 upload phase (completed crawl but still processing)
    if (status.status === 'completed' && status.isProcessing) {
      const uploadProgress =
        status.completed && status.total ? ` (${status.completed}/${status.total} tệp)` : '';
      return {
        text: `Đang lưu dữ liệu...${uploadProgress}${progressText}`,
        icon: <Loader2 className="h-5 w-5 animate-spin text-blue-500" />,
        color: 'text-blue-600',
      };
    }

    switch (status.status) {
      case 'scraping':
        return {
          text: status.isProcessing
            ? `Đang thu thập nội dung trang...${progressText}`
            : `Đang quét cấu trúc website...${progressText}`,
          icon: <Loader2 className="h-5 w-5 animate-spin text-blue-500" />,
          color: 'text-blue-600',
        };
      case 'completed':
        return {
          text: 'Hoàn thành!',
          icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
          color: 'text-green-600',
        };
      case 'failed':
        return {
          text: `Đã xảy ra lỗi${status.error ? `: ${status.error}` : ''}`,
          icon: <XCircle className="h-5 w-5 text-red-500" />,
          color: 'text-red-600',
        };
      case 'blocked':
        return {
          text: 'Website đã chặn truy cập',
          icon: <AlertCircle className="h-5 w-5 text-orange-500" />,
          color: 'text-orange-600',
        };
      default:
        return {
          text: `Đang xử lý...${progressText}`,
          icon: <Loader2 className="h-5 w-5 animate-spin text-blue-500" />,
          color: 'text-blue-600',
        };
    }
  };

  const handleStop = async () => {
    setIsStopping(true);
    try {
      await onStop();
      onClose();
    } finally {
      setIsStopping(false);
      setShowStopConfirm(false);
    }
  };

  const statusDisplay = getStatusDisplay();
  const isInProgress =
    status?.status === 'scraping' ||
    !status ||
    (status?.status === 'completed' && status?.isProcessing);
  const isTerminal =
    (status?.status === 'completed' && !status?.isProcessing) ||
    status?.status === 'failed' ||
    status?.status === 'blocked';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[480px] s3-explr-crawl-progress-dialog">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Thu thập website</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Status message */}
          <div className="flex items-center gap-3">
            {statusDisplay.icon}
            <span className={cn('font-medium', statusDisplay.color)}>{statusDisplay.text}</span>
          </div>

          {/* Progress bar - always show */}
          <div className="space-y-2 s3-explr-crawl-progress-bar">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={cn(
                  'h-3 rounded-full transition-all duration-300 s3-explr-crawl-progress-fill',
                  status?.status === 'completed'
                    ? 'bg-green-500'
                    : status?.status === 'failed'
                      ? 'bg-red-500'
                      : status?.status === 'blocked'
                        ? 'bg-orange-500'
                        : 'bg-blue-600',
                  isInitializing && 's3-explr-crawl-progress-indeterminate',
                )}
                style={{ width: `${displayProgress}%` }}
              />
            </div>
            <div className="text-right text-sm text-gray-600">{displayProgress}%</div>
          </div>

          {/* Details */}
          <div className="space-y-2 text-sm text-gray-700">
            <p className="break-all">
              <span className="font-medium">URL:</span> {currentJob.url}
            </p>
            <p>
              <span className="font-medium">Đã thu thập:</span>{' '}
              {crawledCount === 0 && completed === 0
                ? 'Đang khởi tạo...'
                : `${crawledCount}/${totalCount} trang`}
            </p>
            <p>
              <span className="font-medium">Thời gian:</span> {formatTime(elapsedTime)}
            </p>
          </div>

          {/* Real-time crawled pages list */}
          {pages.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Trang đã thu thập ({pages.length})
                </span>
              </div>
              <div className="max-h-40 overflow-auto rounded-md border border-gray-200 bg-gray-50">
                {pages.map((page, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-0.5 px-2 py-1.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 w-[400px]"
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-medium text-gray-500 flex-shrink-0 w-5 text-right">
                        #{index + 1}
                      </span>
                      <p
                        className="text-[10px] font-medium text-gray-700 flex-1 min-w-0"
                        title={page.title}
                      >
                        {page.title.length > 60 ? page.title.substring(0, 60) + '...' : page.title}
                      </p>
                      <span
                        className={`text-[9px] flex-shrink-0 ${page.status >= 200 && page.status < 300 ? 'text-green-500' : 'text-red-500'}`}
                      >
                        {page.status}
                      </span>
                      <span className="text-[9px] text-gray-400 flex-shrink-0">D:{page.depth}</span>
                    </div>
                    <p className="text-[9px] text-gray-500" title={page.url}>
                      {page.url.length > 60 ? page.url.substring(0, 60) + '...' : page.url}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {showStopConfirm ? (
            <div className="flex items-center gap-2 w-full justify-end">
              <span className="text-sm text-gray-600 mr-2">Xác nhận dừng?</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowStopConfirm(false)}
                disabled={isStopping}
              >
                Hủy
              </Button>
              <Button variant="destructive" size="sm" onClick={handleStop} disabled={isStopping}>
                {isStopping ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Đang dừng...
                  </>
                ) : (
                  'Xác nhận'
                )}
              </Button>
            </div>
          ) : (
            isInProgress && (
              <Button variant="destructive" onClick={() => setShowStopConfirm(true)}>
                Dừng thu thập
              </Button>
            )
          )}
          {isTerminal && (
            <Button variant="outline" onClick={onClose}>
              Đóng
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
